import * as XLSX from "xlsx";
import { PreviewPackage } from "./TaskTemplatePreviewTable";

// ───────────────────────── Types ─────────────────────────

export interface ValidationError {
  sheet: string;
  packageErrors: string[];
  taskErrors: Map<string, string[]>;  // taskTitle -> errors
}

export interface Subtask {
  subtaskTitle: string;
  description: string;
  errors?: string[];
}

export interface NormalTask {
  taskTitle: string;
  deadlineAtDaysBeforeStart?: number;
  deadlineAtDaysBeforeEnd?: number;
  subtasks: Subtask[];
  errors?: string[];
}

export interface InspectionTask {
  taskTitle: string;
  deadlineAtDaysBeforeStart?: number;
  deadlineAtDaysBeforeEnd?: number;
  congridId: string;
  description?: string;
  errors?: string[];
}

export interface DocumentTask {
  taskTitle: string;
  deadlineAtDaysBeforeStart?: number;
  deadlineAtDaysBeforeEnd?: number;
  subtasks: Subtask[];
  errors?: string[];
}

export interface WorkSection {
  workSectionCode: string;
  workSectionName: string;
  inspectionTasks: InspectionTask[];
  documentTasks: DocumentTask[];
  errors?: string[];
}

export interface ProcurementPackage {
  procurementPackageCode: string;
  procurementPackageName: string;
  tasks: NormalTask[];
  workSections: WorkSection[];
  errors?: string[];
}

export interface ExcelData {
  procurementPackages: ProcurementPackage[];
}

// ───────────────────────── Constants ─────────────────────────

const HANKINTAPAKETIT_SHEET = "Hankintapaketit";
const TYOVAIHEET_SHEET = "Työvaiheet";
const MAARAAJAT_SHEET = "Määräajat";
const LIITEDOKUMENTIT_SHEET = "Liitedokumentit";

const META_SHEETS = [
  HANKINTAPAKETIT_SHEET,
  TYOVAIHEET_SHEET,
  MAARAAJAT_SHEET,
  LIITEDOKUMENTIT_SHEET,
];

const IGNORE_SHEETS = ["Työnjohtajan tehtävälista", ...META_SHEETS];

const HANKINTAPAKETIT_HEADERS = ["Hankintapakettikoodi", "Hankintapakettinimi"];
const TYOVAIHEET_HEADERS = [
  "Työvaihekoodi",
  "Työvaihenimike",
  "Hankintapakettikoodi",
  "Hankintapakettinimi",
  "Mestan vastaanotto",
  "Malliasennustarkastus",
  "Osakohteen tarkastus",
  "Työvaiheenvastaanotto",
];
const MAARAAJAT_HEADERS = [
  "Tehtävänimi",
  "Tehty X päivää ennen työn alkamista",
  "Tehty X päivää ennen työn päättymistä",
];
const LIITEDOKUMENTIT_HEADERS = [
  "Työvaihekoodi",
  "Työvaihenimi",
  "Liitedokumenttinimi",
  "Kuvaus",
];

const NORMAL_TASK_TITLE_HEADER = "Tehtävänimi";
const NORMAL_SUBTASK_HEADERS = ["Alitehtävä", "Kuvaus"];
const NORMAL_SUBTASK_HEADER_REFERENCES = ["Alitehtävä-otsikon", "Kuvaus-otsikon"];

const NORMAL_TASK_TITLES = [
  "Suunnitelmiin perehtyminen",
  "Aliurakkasopimukseen perehtyminen",
  "Aloitusedellytykset",
  "Taloudellinen loppuselvitys",
];

const CONGRID_TASK_TITLES = [
  "Mestan vastaanotto",
  "Malliasennustarkastus",
  "Osakohteen tarkastus",
  "Työvaiheenvastaanotto",
];
// Column indices in Työvaiheet for the four inspection ID columns (E=4, F=5, G=6, H=7).
const TYOVAIHEET_INSPECTION_COLS = [4, 5, 6, 7];

const DOCUMENT_TASK_TITLE = "Liitedokumentit";

// Normal task column blocks on procurement-package sheets.
// Title column doubles as the subtask-title column; the adjacent column is the subtask-description column.
const NORMAL_TASK_COLS = [
  { title: 1, subtaskTitle: 1, subtaskDescription: 2 },    // B / C
  { title: 5, subtaskTitle: 5, subtaskDescription: 6 },    // F / G
  { title: 9, subtaskTitle: 9, subtaskDescription: 10 },   // J / K
  { title: 13, subtaskTitle: 13, subtaskDescription: 14 }, // N / O
];

const TASK_HEADER_ROW = 4;
const TASK_DATA_ROW = 5;
const SUBTASK_HEADER_ROW = 6;
const SUBTASK_DATA_START_ROW = 7;
const META_HEADER_ROW = 1;
const META_DATA_START_ROW = 2;
const MAX_DATA_ROWS = 500;

const EXCEL_EXTENSIONS = [".xls", ".xlsx", ".xlsm", ".xlt", ".xltx"];
const EXCEL_MIME_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.template.macroenabled.12",
];

// ───────────────────────── Helpers ─────────────────────────

function normalizeString(str: string): string {
  if (!str) return str;
  return String(str).normalize("NFC");
}

function rawCell(sheet: XLSX.WorkSheet, ref: string): unknown {
  return sheet[ref]?.v;
}

function cellStr(sheet: XLSX.WorkSheet, ref: string): string {
  const v = sheet[ref]?.v;
  return v === undefined || v === null ? "" : String(v).trim();
}

function isNonEmpty(v: unknown): boolean {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function pushTaskErr(map: Map<string, string[]>, key: string, msg: string) {
  if (!map.has(key)) map.set(key, []);
  map.get(key)!.push(msg);
}

// ───────────────────────── Meta-sheet readers ─────────────────────────

interface MaaraaikaEntry {
  startRaw: unknown;
  endRaw: unknown;
}

interface TyovaiheRow {
  rowNum: number;
  workSectionCode: string;
  workSectionName: string;
  procurementPackageCode: string;
  inspectionIds: Partial<Record<string, string>>;
}

interface LiitedokumentitRow {
  rowNum: number;
  workSectionCode: string;
  subtaskTitle: string;
  description: string;
}

function readHankintapaketit(sheet: XLSX.WorkSheet | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!sheet) return map;
  for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
    const code = cellStr(sheet, `A${row}`);
    const name = cellStr(sheet, `B${row}`);
    if (!code && !name) break;
    if (code) map.set(normalizeString(code), normalizeString(name));
  }
  return map;
}

function readMaaraajat(sheet: XLSX.WorkSheet | undefined): Map<string, MaaraaikaEntry> {
  const map = new Map<string, MaaraaikaEntry>();
  if (!sheet) return map;
  for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
    const name = cellStr(sheet, `A${row}`);
    const startRaw = rawCell(sheet, `B${row}`);
    const endRaw = rawCell(sheet, `C${row}`);
    if (!name && !isNonEmpty(startRaw) && !isNonEmpty(endRaw)) break;
    if (!name) continue;
    map.set(normalizeString(name), { startRaw, endRaw });
  }
  return map;
}

function readTyovaiheet(sheet: XLSX.WorkSheet | undefined): TyovaiheRow[] {
  const rows: TyovaiheRow[] = [];
  if (!sheet) return rows;
  for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
    const code = cellStr(sheet, `A${row}`);
    const name = cellStr(sheet, `B${row}`);
    const ppCode = cellStr(sheet, `C${row}`);
    const inspectionIds: Partial<Record<string, string>> = {};
    let anyId = false;
    for (let i = 0; i < CONGRID_TASK_TITLES.length; i++) {
      const colLetter = XLSX.utils.encode_col(TYOVAIHEET_INSPECTION_COLS[i]);
      const id = cellStr(sheet, `${colLetter}${row}`);
      if (id) {
        inspectionIds[CONGRID_TASK_TITLES[i]] = normalizeString(id);
        anyId = true;
      }
    }
    if (!code && !name && !ppCode && !anyId) break;
    rows.push({
      rowNum: row,
      workSectionCode: normalizeString(code),
      workSectionName: normalizeString(name),
      procurementPackageCode: normalizeString(ppCode),
      inspectionIds,
    });
  }
  return rows;
}

function readLiitedokumentit(sheet: XLSX.WorkSheet | undefined): LiitedokumentitRow[] {
  const rows: LiitedokumentitRow[] = [];
  if (!sheet) return rows;
  for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
    const code = cellStr(sheet, `A${row}`);
    // Column B (Työvaihenimi) is intentionally ignored — name comes from Työvaiheet.
    const title = cellStr(sheet, `C${row}`);
    const desc = cellStr(sheet, `D${row}`);
    if (!code && !title && !desc) break;
    rows.push({
      rowNum: row,
      workSectionCode: normalizeString(code),
      subtaskTitle: normalizeString(title),
      description: normalizeString(desc),
    });
  }
  return rows;
}

interface ResolvedDeadline {
  start?: number;
  end?: number;
  error?: string;
}

function resolveDeadline(
  taskName: string,
  maaraajat: Map<string, MaaraaikaEntry>
): ResolvedDeadline {
  const entry = maaraajat.get(taskName);
  if (!entry) {
    return { error: `Tehtävälle "${taskName}" ei löydy määräaikaa Määräajat-välilehdeltä.` };
  }
  const hasStart = isNonEmpty(entry.startRaw);
  const hasEnd = isNonEmpty(entry.endRaw);
  if (hasStart && hasEnd) {
    return { error: `Vain toinen määräaika-solu saa olla täytetty tehtävälle "${taskName}" Määräajat-välilehdellä.` };
  }
  if (!hasStart && !hasEnd) {
    return { error: `Toinen määräaika-solu pitää olla täytetty tehtävälle "${taskName}" Määräajat-välilehdellä.` };
  }
  const raw = hasStart ? entry.startRaw : entry.endRaw;
  const v = Number(raw);
  if (!Number.isInteger(v)) {
    return { error: `Tehtävän "${taskName}" määräaika pitää olla kokonaisluku, löytyi "${raw}" Määräajat-välilehdellä.` };
  }
  return hasStart ? { start: v } : { end: v };
}

function validateMetaHeaders(
  sheet: XLSX.WorkSheet | undefined,
  sheetName: string,
  expected: string[]
): string[] {
  const errors: string[] = [];
  if (!sheet) {
    errors.push(`Välilehti "${sheetName}" puuttuu tiedostosta.`);
    return errors;
  }
  for (let i = 0; i < expected.length; i++) {
    const colLetter = XLSX.utils.encode_col(i);
    const v = cellStr(sheet, `${colLetter}${META_HEADER_ROW}`);
    if (v !== expected[i]) {
      const found = v || "(tyhjä)";
      errors.push(`Otsikon "${expected[i]}" pitäisi olla solussa ${colLetter}${META_HEADER_ROW}, löytyi "${found}".`);
    }
  }
  return errors;
}

// ───────────────────────── Validation ─────────────────────────

export async function validateExcelFile(file: File): Promise<ValidationError[]> {
  const validationErrors: ValidationError[] = [];

  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
      cellFormula: false,
      cellNF: true,
    });

    const hpSheet = workbook.Sheets[HANKINTAPAKETIT_SHEET];
    const tvSheet = workbook.Sheets[TYOVAIHEET_SHEET];
    const maSheet = workbook.Sheets[MAARAAJAT_SHEET];
    const ldSheet = workbook.Sheets[LIITEDOKUMENTIT_SHEET];

    const hpErrors = validateMetaHeaders(hpSheet, HANKINTAPAKETIT_SHEET, HANKINTAPAKETIT_HEADERS);
    const tvErrors = validateMetaHeaders(tvSheet, TYOVAIHEET_SHEET, TYOVAIHEET_HEADERS);
    const maErrors = validateMetaHeaders(maSheet, MAARAAJAT_SHEET, MAARAAJAT_HEADERS);
    const ldErrors = validateMetaHeaders(ldSheet, LIITEDOKUMENTIT_SHEET, LIITEDOKUMENTIT_HEADERS);

    const hankintapaketit = readHankintapaketit(hpSheet);
    const maaraajat = readMaaraajat(maSheet);
    const tyovaiheet = readTyovaiheet(tvSheet);
    const liitedokumentit = readLiitedokumentit(ldSheet);
    const tyovaiheByCode = new Map(tyovaiheet.map((t) => [t.workSectionCode, t]));

    // Per-row validation: Hankintapaketit
    if (hpSheet) {
      for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
        const code = cellStr(hpSheet, `A${row}`);
        const name = cellStr(hpSheet, `B${row}`);
        if (!code && !name) break;
        if (!code) hpErrors.push(`Hankintapakettikoodi puuttuu solusta A${row}.`);
        else if (!name) hpErrors.push(`Hankintapaketin nimi puuttuu solusta B${row} (koodi "${code}").`);
      }
    }

    // Per-row validation: Määräajat
    if (maSheet) {
      for (let row = META_DATA_START_ROW; row <= META_DATA_START_ROW + MAX_DATA_ROWS; row++) {
        const name = cellStr(maSheet, `A${row}`);
        const startRaw = rawCell(maSheet, `B${row}`);
        const endRaw = rawCell(maSheet, `C${row}`);
        if (!name && !isNonEmpty(startRaw) && !isNonEmpty(endRaw)) break;
        if (!name) {
          maErrors.push(`Tehtävänimi puuttuu solusta A${row}.`);
          continue;
        }
        const hasStart = isNonEmpty(startRaw);
        const hasEnd = isNonEmpty(endRaw);
        if (hasStart && hasEnd) {
          maErrors.push(`Vain toinen määräaika-solu saa olla täytetty tehtävälle "${name}" (solut B${row} ja C${row}).`);
        } else if (!hasStart && !hasEnd) {
          maErrors.push(`Toinen määräaika-solu pitää olla täytetty tehtävälle "${name}" (joko B${row} tai C${row}).`);
        } else {
          const raw = hasStart ? startRaw : endRaw;
          const v = Number(raw);
          if (!Number.isInteger(v)) {
            const cell = hasStart ? `B${row}` : `C${row}`;
            maErrors.push(`Tehtävän "${name}" määräaika pitää olla kokonaisluku, löytyi "${raw}" (solu ${cell}).`);
          }
        }
      }
    }

    // Per-row validation: Työvaiheet
    if (tvSheet) {
      for (const t of tyovaiheet) {
        const r = t.rowNum;
        if (!t.workSectionCode) tvErrors.push(`Työvaihekoodi puuttuu solusta A${r}.`);
        if (!t.workSectionName) tvErrors.push(`Työvaihenimike puuttuu solusta B${r}.`);
        if (!t.procurementPackageCode) {
          tvErrors.push(`Hankintapakettikoodi puuttuu solusta C${r}.`);
        } else if (!hankintapaketit.has(t.procurementPackageCode)) {
          tvErrors.push(`Hankintapakettikoodia "${t.procurementPackageCode}" (solu C${r}) ei löydy Hankintapaketit-välilehdeltä.`);
        }
        for (let i = 0; i < CONGRID_TASK_TITLES.length; i++) {
          const id = t.inspectionIds[CONGRID_TASK_TITLES[i]];
          if (id && !/^\d{7}$/.test(id)) {
            const colLetter = XLSX.utils.encode_col(TYOVAIHEET_INSPECTION_COLS[i]);
            tvErrors.push(`Congrid ID:n pitää olla tasan 7 numeroa, löytyi "${id}" (solu ${colLetter}${r}).`);
          }
        }
      }
    }

    // Per-row validation: Liitedokumentit
    if (ldSheet) {
      for (const l of liitedokumentit) {
        const r = l.rowNum;
        if (!l.workSectionCode) {
          ldErrors.push(`Työvaihekoodi puuttuu solusta A${r}.`);
        } else if (!tyovaiheByCode.has(l.workSectionCode)) {
          ldErrors.push(`Työvaihekoodia "${l.workSectionCode}" (solu A${r}) ei löydy Työvaiheet-välilehdeltä.`);
        }
        if (!l.subtaskTitle) {
          ldErrors.push(`Liitedokumenttinimi puuttuu solusta C${r}.`);
        }
      }
    }

    if (hpErrors.length) validationErrors.push({ sheet: HANKINTAPAKETIT_SHEET, packageErrors: hpErrors, taskErrors: new Map() });
    if (tvErrors.length) validationErrors.push({ sheet: TYOVAIHEET_SHEET, packageErrors: tvErrors, taskErrors: new Map() });
    if (maErrors.length) validationErrors.push({ sheet: MAARAAJAT_SHEET, packageErrors: maErrors, taskErrors: new Map() });
    if (ldErrors.length) validationErrors.push({ sheet: LIITEDOKUMENTIT_SHEET, packageErrors: ldErrors, taskErrors: new Map() });

    const ppSheetNames = workbook.SheetNames.filter((n) => !IGNORE_SHEETS.includes(n));

    if (ppSheetNames.length === 0 && tyovaiheet.length === 0) {
      validationErrors.push({
        sheet: "(koko tiedosto)",
        packageErrors: ["Tiedostossa ei ole yhtään hankintapakettilehteä eikä Työvaiheet-rivejä."],
        taskErrors: new Map(),
      });
      return validationErrors;
    }

    // Validate each procurement-package sheet
    for (const sheetName of ppSheetNames) {
      const packageErrors: string[] = [];
      const taskErrors = new Map<string, string[]>();
      const pp = workbook.Sheets[sheetName];

      if (!hankintapaketit.has(sheetName)) {
        packageErrors.push(`Hankintapakettikoodia "${sheetName}" ei löydy Hankintapaketit-välilehdeltä.`);
      }

      // Normal tasks
      for (let i = 0; i < NORMAL_TASK_COLS.length; i++) {
        const cols = NORMAL_TASK_COLS[i];
        const titleCol = XLSX.utils.encode_col(cols.title);
        const subtaskTitleCol = XLSX.utils.encode_col(cols.subtaskTitle);
        const subtaskDescriptionCol = XLSX.utils.encode_col(cols.subtaskDescription);
        const expectedTitle = NORMAL_TASK_TITLES[i];

        // Skip task if no subtasks present at all
        let hasAnySubtask = false;
        for (let row = SUBTASK_DATA_START_ROW; row <= SUBTASK_DATA_START_ROW + MAX_DATA_ROWS; row++) {
          const st = cellStr(pp, `${subtaskTitleCol}${row}`);
          const sd = cellStr(pp, `${subtaskDescriptionCol}${row}`);
          if (!st && !sd) break;
          if (st || sd) { hasAnySubtask = true; break; }
        }
        if (!hasAnySubtask) continue;

        // Task title cell
        const taskTitle = cellStr(pp, `${titleCol}${TASK_DATA_ROW}`);
        if (taskTitle !== expectedTitle) {
          const found = taskTitle || "(tyhjä)";
          pushTaskErr(taskErrors, expectedTitle, `Tehtävänimi pitäisi olla "${expectedTitle}", löytyi "${found}" (solu ${titleCol}${TASK_DATA_ROW}).`);
        }

        // Task-name header
        const titleHeader = cellStr(pp, `${titleCol}${TASK_HEADER_ROW}`);
        if (titleHeader !== NORMAL_TASK_TITLE_HEADER) {
          const found = titleHeader || "(tyhjä)";
          pushTaskErr(taskErrors, expectedTitle, `Tehtävänimi-otsikon pitäisi olla "${NORMAL_TASK_TITLE_HEADER}", löytyi "${found}" (solu ${titleCol}${TASK_HEADER_ROW}).`);
        }

        // Subtask headers
        const stHeader = cellStr(pp, `${subtaskTitleCol}${SUBTASK_HEADER_ROW}`);
        const sdHeader = cellStr(pp, `${subtaskDescriptionCol}${SUBTASK_HEADER_ROW}`);
        if (stHeader !== NORMAL_SUBTASK_HEADERS[0]) {
          const found = stHeader || "(tyhjä)";
          pushTaskErr(taskErrors, expectedTitle, `${NORMAL_SUBTASK_HEADER_REFERENCES[0]} pitäisi olla "${NORMAL_SUBTASK_HEADERS[0]}", löytyi "${found}" (solu ${subtaskTitleCol}${SUBTASK_HEADER_ROW}).`);
        }
        if (sdHeader !== NORMAL_SUBTASK_HEADERS[1]) {
          const found = sdHeader || "(tyhjä)";
          pushTaskErr(taskErrors, expectedTitle, `${NORMAL_SUBTASK_HEADER_REFERENCES[1]} pitäisi olla "${NORMAL_SUBTASK_HEADERS[1]}", löytyi "${found}" (solu ${subtaskDescriptionCol}${SUBTASK_HEADER_ROW}).`);
        }

        // Orphan description (description without title)
        for (let row = SUBTASK_DATA_START_ROW; row <= SUBTASK_DATA_START_ROW + MAX_DATA_ROWS; row++) {
          const st = cellStr(pp, `${subtaskTitleCol}${row}`);
          const sd = cellStr(pp, `${subtaskDescriptionCol}${row}`);
          if (!st && !sd) break;
          if (sd && !st) {
            pushTaskErr(taskErrors, expectedTitle, `Solu ${subtaskDescriptionCol}${row} ei voi olla täytetty ilman alitehtävän nimeä (solu ${subtaskTitleCol}${row}).`);
            break;
          }
        }

        // Deadline must exist in Määräajat
        const dl = resolveDeadline(expectedTitle, maaraajat);
        if (dl.error) pushTaskErr(taskErrors, expectedTitle, dl.error);
      }

      // Inspection-task deadlines (any title used by at least one work section of this pp)
      const wsForThisPp = tyovaiheet.filter((t) => t.procurementPackageCode === sheetName);
      const usedInspectionTitles = new Set<string>();
      for (const ws of wsForThisPp) {
        for (const title of CONGRID_TASK_TITLES) {
          if (ws.inspectionIds[title]) usedInspectionTitles.add(title);
        }
      }
      for (const title of usedInspectionTitles) {
        const dl = resolveDeadline(title, maaraajat);
        if (dl.error) pushTaskErr(taskErrors, title, dl.error);
      }

      // Document-task deadline (any ws of this pp has matching Liitedokumentit rows)
      const wsCodesForThisPp = new Set(wsForThisPp.map((w) => w.workSectionCode));
      const hasDocs = liitedokumentit.some((l) => l.workSectionCode && wsCodesForThisPp.has(l.workSectionCode));
      if (hasDocs) {
        const dl = resolveDeadline(DOCUMENT_TASK_TITLE, maaraajat);
        if (dl.error) pushTaskErr(taskErrors, DOCUMENT_TASK_TITLE, dl.error);
      }

      if (packageErrors.length > 0 || taskErrors.size > 0) {
        validationErrors.push({ sheet: sheetName, packageErrors, taskErrors });
      }
    }

    return validationErrors;
  } catch (error) {
    throw new Error(
      "Tiedoston käsittelyvirhe: " + (error instanceof Error ? error.message : String(error))
    );
  }
}

// ───────────────────────── Import ─────────────────────────

export async function importExcelData(file: File, validationErrors: ValidationError[] = []): Promise<ExcelData> {
  const procurementPackages: ProcurementPackage[] = [];

  const errorsBySheet = new Map<string, { packageErrors: string[], taskErrors: Map<string, string[]> }>();
  validationErrors.forEach((e) => {
    errorsBySheet.set(e.sheet, { packageErrors: e.packageErrors, taskErrors: e.taskErrors });
  });

  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
      cellFormula: false,
      cellNF: true,
    });

    const hankintapaketit = readHankintapaketit(workbook.Sheets[HANKINTAPAKETIT_SHEET]);
    const maaraajat = readMaaraajat(workbook.Sheets[MAARAAJAT_SHEET]);
    const tyovaiheet = readTyovaiheet(workbook.Sheets[TYOVAIHEET_SHEET]);
    const liitedokumentit = readLiitedokumentit(workbook.Sheets[LIITEDOKUMENTIT_SHEET]);

    // Group work sections by pp code (preserves order from Työvaiheet)
    const wsByPpCode = new Map<string, TyovaiheRow[]>();
    for (const t of tyovaiheet) {
      if (!t.procurementPackageCode || !t.workSectionCode) continue;
      if (!wsByPpCode.has(t.procurementPackageCode)) wsByPpCode.set(t.procurementPackageCode, []);
      wsByPpCode.get(t.procurementPackageCode)!.push(t);
    }

    // Group Liitedokumentit subtasks by work-section code (preserves order)
    const docsByWsCode = new Map<string, LiitedokumentitRow[]>();
    for (const l of liitedokumentit) {
      if (!l.workSectionCode || !l.subtaskTitle) continue;
      if (!docsByWsCode.has(l.workSectionCode)) docsByWsCode.set(l.workSectionCode, []);
      docsByWsCode.get(l.workSectionCode)!.push(l);
    }

    // Pp codes to emit: sheets and Työvaiheet-referenced pps that exist in Hankintapaketit.
    // Sheets are kept even if missing from Hankintapaketit, so the validation error surfaces with context.
    const ppSheetNames = workbook.SheetNames.filter((n) => !IGNORE_SHEETS.includes(n));
    const ppCodesInUse: string[] = [];
    const seen = new Set<string>();
    for (const name of ppSheetNames) {
      if (!seen.has(name)) { ppCodesInUse.push(name); seen.add(name); }
    }
    for (const t of tyovaiheet) {
      const code = t.procurementPackageCode;
      if (code && hankintapaketit.has(code) && !seen.has(code)) {
        ppCodesInUse.push(code);
        seen.add(code);
      }
    }

    const docDeadline = resolveDeadline(DOCUMENT_TASK_TITLE, maaraajat);
    const inspectionDeadlines: Record<string, ResolvedDeadline> = {};
    for (const title of CONGRID_TASK_TITLES) {
      inspectionDeadlines[title] = resolveDeadline(title, maaraajat);
    }

    for (const ppCode of ppCodesInUse) {
      const ppSheet = workbook.Sheets[ppCode];
      const ppName = hankintapaketit.get(ppCode) ?? "";
      const sheetErrors = errorsBySheet.get(ppCode);
      const packageErrors = sheetErrors?.packageErrors || [];
      const taskErrors = sheetErrors?.taskErrors || new Map<string, string[]>();

      // Normal tasks (only meaningful when a pp sheet exists)
      const tasks: NormalTask[] = [];
      if (ppSheet) {
        for (let i = 0; i < NORMAL_TASK_COLS.length; i++) {
          const cols = NORMAL_TASK_COLS[i];
          const titleCol = XLSX.utils.encode_col(cols.title);
          const subtaskTitleCol = XLSX.utils.encode_col(cols.subtaskTitle);
          const subtaskDescriptionCol = XLSX.utils.encode_col(cols.subtaskDescription);
          const expectedTitle = NORMAL_TASK_TITLES[i];

          const subtasks: Subtask[] = [];
          for (let row = SUBTASK_DATA_START_ROW; row <= SUBTASK_DATA_START_ROW + MAX_DATA_ROWS; row++) {
            const st = normalizeString(String(ppSheet[`${subtaskTitleCol}${row}`]?.v ?? ""));
            const sd = normalizeString(String(ppSheet[`${subtaskDescriptionCol}${row}`]?.v ?? ""));
            if (!st && !sd) break;
            subtasks.push({ subtaskTitle: st, description: sd });
          }
          if (subtasks.length === 0) continue;

          const rawTitle = normalizeString(String(ppSheet[`${titleCol}${TASK_DATA_ROW}`]?.v ?? ""));
          const taskTitle = rawTitle || expectedTitle;
          const dl = resolveDeadline(expectedTitle, maaraajat);
          const taskErrorList = taskErrors.get(expectedTitle) || [];

          tasks.push({
            taskTitle,
            deadlineAtDaysBeforeStart: dl.start,
            deadlineAtDaysBeforeEnd: dl.end,
            subtasks,
            errors: taskErrorList.length > 0 ? taskErrorList : undefined,
          });
        }
      }

      // Work sections from Työvaiheet
      const workSections: WorkSection[] = [];
      const wsForThisPp = wsByPpCode.get(ppCode) || [];

      for (const t of wsForThisPp) {
        const inspectionTasks: InspectionTask[] = [];
        for (const title of CONGRID_TASK_TITLES) {
          const id = t.inspectionIds[title];
          if (!id) continue;
          const dl = inspectionDeadlines[title];
          const taskErrorList = taskErrors.get(title) || [];
          inspectionTasks.push({
            taskTitle: title,
            deadlineAtDaysBeforeStart: dl.start,
            deadlineAtDaysBeforeEnd: dl.end,
            congridId: id,
            errors: taskErrorList.length > 0 ? taskErrorList : undefined,
          });
        }

        const documentTasks: DocumentTask[] = [];
        const docRows = docsByWsCode.get(t.workSectionCode) || [];
        if (docRows.length > 0) {
          const subtasks: Subtask[] = docRows.map((d) => ({
            subtaskTitle: d.subtaskTitle,
            description: d.description,
          }));
          const taskErrorList = taskErrors.get(DOCUMENT_TASK_TITLE) || [];
          documentTasks.push({
            taskTitle: DOCUMENT_TASK_TITLE,
            deadlineAtDaysBeforeStart: docDeadline.start,
            deadlineAtDaysBeforeEnd: docDeadline.end,
            subtasks,
            errors: taskErrorList.length > 0 ? taskErrorList : undefined,
          });
        }

        if (inspectionTasks.length > 0 || documentTasks.length > 0) {
          workSections.push({
            workSectionCode: t.workSectionCode,
            workSectionName: t.workSectionName,
            inspectionTasks,
            documentTasks,
          });
        }
      }

      procurementPackages.push({
        procurementPackageCode: ppCode,
        procurementPackageName: ppName,
        tasks,
        workSections,
        errors: packageErrors.length > 0 ? packageErrors : undefined,
      });
    }

    return { procurementPackages };
  } catch (error) {
    throw new Error(
      "Tietojen tuontivirhe: " + (error instanceof Error ? error.message : String(error))
    );
  }
}

// ───────────────────────── Public helpers ─────────────────────────

export function isExcelFile(f: File): boolean {
  return (
    EXCEL_MIME_TYPES.includes(f.type) ||
    EXCEL_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
  );
}

function deadlineText(task: { deadlineAtDaysBeforeStart?: number; deadlineAtDaysBeforeEnd?: number }): string {
  if (task.deadlineAtDaysBeforeStart !== undefined) {
    const d = task.deadlineAtDaysBeforeStart;
    return d < 0 ? `${-d} pv alun jälkeen` : d === 0 ? "alkupäivänä" : `${d} pv ennen alkua`;
  }
  if (task.deadlineAtDaysBeforeEnd !== undefined) {
    const d = task.deadlineAtDaysBeforeEnd;
    return d < 0 ? `${-d} pv lopun jälkeen` : d === 0 ? "loppupäivänä" : `${d} pv ennen loppua`;
  }
  return "—";
}

export function toPreviewPackages(data: ExcelData): PreviewPackage[] {
  return data.procurementPackages.map((pp) => ({
    id: pp.procurementPackageCode,
    code: pp.procurementPackageCode,
    name: pp.procurementPackageName,
    errors: pp.errors,
    tasks: pp.tasks.map((t, i) => ({
      id: `${pp.procurementPackageCode}-task-${i}`,
      name: t.taskTitle,
      deadlineText: deadlineText(t),
      errors: t.errors,
      subtasks: t.subtasks.map((s, si) => ({
        id: `${pp.procurementPackageCode}-task-${i}-sub-${si}`,
        name: s.subtaskTitle,
        description: s.description,
      })),
    })),
    workSections: pp.workSections.map((ws) => ({
      id: ws.workSectionCode,
      code: ws.workSectionCode,
      name: ws.workSectionName,
      errors: ws.errors,
      inspectionTasks: ws.inspectionTasks.map((wt, ti) => ({
        id: `${pp.procurementPackageCode}-${ws.workSectionCode}-insp-${ti}`,
        name: wt.taskTitle,
        description: wt.description ?? "",
        deadlineText: deadlineText(wt),
        congridId: wt.congridId,
        errors: wt.errors,
      })),
      documentTasks: ws.documentTasks.map((dt, ti) => ({
        id: `${pp.procurementPackageCode}-${ws.workSectionCode}-doc-${ti}`,
        name: dt.taskTitle,
        deadlineText: deadlineText(dt),
        subtasks: dt.subtasks.map((s, si) => ({
          id: `${pp.procurementPackageCode}-${ws.workSectionCode}-doc-${ti}-sub-${si}`,
          name: s.subtaskTitle,
          description: s.description,
        })),
        errors: dt.errors,
      })),
    })),
  }));
}
